import formQuestionsToFormWithTree from '@opsbase/lib-form/convert/formQuestionsToFormWithTree';
import getPercentCompletion from '@opsbase/lib-form/helpers/getPercentCompletion';
import GradingMethodType from '@opsbase/lib-grading/GradingMethodType';
import i18n from '@opsbase/lib-i18n';
import calcOpStatus from '@opsbase/lib-op/helpers/calcOpStatus';
import opIs from '@opsbase/lib-op/helpers/opIs';
import OpStatus, { opStatusColor } from '@opsbase/lib-op/OpStatus';
import Pages from '@opsbase/lib-pages';
import Palette from '@opsbase/lib-palette';
import { NiceDateFormat, SimpleTimeFormat } from '@opsbase/lib-time/consts';
import shiftTZXToLocal from '@opsbase/lib-time/helpers/shiftTZXToLocal';
import { humanizeFromNow, logStamp } from '@opsbase/lib-time/utils';
import useSelf from '@opsbase/react-ctrl-self/useSelf';
import RosetteIcon from '@opsbase/web-icons/icons/RosetteIcon';
import TickIcon from '@opsbase/web-icons/icons/TickIcon';
import useNav from '@opsbase/web-nav/useNav';
import React, { useCallback, useMemo } from 'react';
import ToDoCard from './ToDoCard';
export function OpToDoCard(props) {
    const { item: op } = props;
    const { goTo } = useNav();
    const self = useSelf();
    const handleCancel = useCallback(() => {
        goTo(Pages.OP_CANCEL, { id: op.id });
    }, [goTo, op]);
    const handleClick = useCallback(() => {
        if (opIs.performable(op))
            return goTo(Pages.OP_PERFORM, { id: op.id });
        return goTo(Pages.OP_VIEW, { id: op.id });
    }, [goTo, op]);
    const cardProps = useMemo(() => {
        var _a, _b, _c;
        return ({
            id: op.id,
            title: op.title,
            description: op.description,
            watermark: op.isScoreEnabled ? RosetteIcon : TickIcon,
            statusLabel: calcStatusLabel(op),
            statusColor: calcStatusColor(op),
            scoreGradePass: calcScoreGradePass(op, self),
            percComplete: calcPercComplete(op),
            stampLabel: calcStampLabel(op),
            place: (_a = op.place) === null || _a === void 0 ? void 0 : _a.title,
            tags: op.type ? [(_b = self.settings) === null || _b === void 0 ? void 0 : _b.getOpTypeTitle(op.type)] : [],
            assignedTo: calcAssignedTo(op),
            dueLabel: calcDueLabel(op),
            startableLabel: calcStartableLabel(op),
            dueTip: calcDueTip(op),
            dueColor: calcDueColor(op),
            updatedLabel: calcUpdatedLabel(op),
            updatedTip: calcUpdatedTip(op),
            isLate: opIs.late(op),
            isClosed: opIs.closed(op),
            isHighlighted: calcIsHighlighted(op, self),
            isCancelable: calcIsCancelable(op, self),
            noOfIssues: (_c = op.issues) === null || _c === void 0 ? void 0 : _c.length,
        });
    }, [op, self]);
    return React.createElement(ToDoCard, { ...cardProps, onClick: handleClick, onCancel: handleCancel });
}
export default OpToDoCard;
function calcIsCancelable(op, self) {
    var _a;
    return (opIs.onDemand(op) && opIs.performable(op) && (!op.createdBy || ((_a = op.createdBy) === null || _a === void 0 ? void 0 : _a.id) === self.id));
}
function calcStatusLabel(op) {
    switch (calcOpStatus(op)) {
        case OpStatus.NOT_DUE:
            return i18n.t('coming-soon');
        case OpStatus.DUE:
        case OpStatus.OVERDUE_NOTSTARTED:
            return i18n.t('todo');
        case OpStatus.STARTED:
        case OpStatus.OVERDUE_STARTED:
            return i18n.t('started');
        case OpStatus.PENDING:
            return i18n.t('pending');
        case OpStatus.COMPLETED:
            return i18n.t('done');
        case OpStatus.COMPLETED_LATE:
            return i18n.t('done');
        case OpStatus.MISSED:
            return i18n.t('missed');
    }
}
function calcStatusColor(op) {
    return opStatusColor(op.status);
}
function calcIsHighlighted(op, self) {
    var _a, _b;
    return ((opIs.approvable(op) || opIs.performable(op)) &&
        (((_a = op.updatedBy) === null || _a === void 0 ? void 0 : _a.id) === self.id || !!((_b = op.staff) === null || _b === void 0 ? void 0 : _b.find(s => s.id === self.id))));
}
function calcDueLabel(op) {
    if (opIs.submitted(op) || opIs.closed(op) || opIs.missed(op))
        return i18n.t('was-due') + ' ' + humanizeFromNow(op.completeBy);
    return i18n.t('due') + ' ' + humanizeFromNow(op.completeBy);
}
function calcStartableLabel(op) {
    if (opIs.submitted(op) || opIs.closed(op) || opIs.missed(op) || opIs.performable(op))
        return '';
    return i18n.t('can-be-started') + ' ' + humanizeFromNow(op.startFrom);
}
function calcDueTip(op) {
    var _a, _b;
    const due = shiftTZXToLocal(op.completeBy, ((_a = op.place) === null || _a === void 0 ? void 0 : _a.timezone) || 'UTC');
    return ((due === null || due === void 0 ? void 0 : due.format(NiceDateFormat)) +
        ' @ ' +
        (due === null || due === void 0 ? void 0 : due.format(SimpleTimeFormat)) +
        '\n' +
        (((_b = op.place) === null || _b === void 0 ? void 0 : _b.timezone) || 'UTC'));
}
function calcDueColor(op) {
    if (!opIs.submitted(op) && opIs.late(op))
        return Palette.OJ;
    return Palette.BlackCoral;
}
function calcPercComplete(op) {
    return opIs.started(op) && !opIs.submitted(op)
        ? getPercentCompletion(formQuestionsToFormWithTree(op.form))
        : undefined;
}
function calcScoreGradePass(op, self) {
    var _a, _b;
    if (!opIs.submitted(op))
        return undefined;
    if (!op.isScoreEnabled)
        return undefined;
    if (!(op.isScorePublic || self.isAdmin || self.isPrimary))
        return undefined;
    return {
        total: op.finalScoreTotal,
        max: op.finalScoreMax,
        grade: op.isGradingPublic &&
            ((_a = op.gradingMethod) === null || _a === void 0 ? void 0 : _a.gradingMethodType) === GradingMethodType.CALCULATED
            ? op.grade
            : undefined,
        passStatus: op.isGradingPublic && ((_b = op.gradingMethod) === null || _b === void 0 ? void 0 : _b.gradingMethodType) !== GradingMethodType.NONE
            ? op.passFailStatus
            : undefined,
    };
}
function calcUpdatedLabel(op) {
    var _a, _b;
    // Closed Ops
    if (!!op.closedDate) {
        if (!!op.approvedDate && ((_a = op.approvedBy) === null || _a === void 0 ? void 0 : _a.id) !== ((_b = op.submittedBy) === null || _b === void 0 ? void 0 : _b.id))
            return logStamp(op.approvedDate, i18n.t('approved'));
        if (opIs.missed(op))
            return logStamp(op.closedDate, i18n.t('missed'));
        return logStamp(op.closedDate, i18n.t('completed'));
    }
    // Open Ops
    if (!!op.awaitingApproval)
        return logStamp(op.actualComplete, i18n.t('submitted'));
    if (!!op.updatedDate)
        return logStamp(op.updatedDate, i18n.t('updated'));
    return logStamp(op.createdDate, i18n.t('created'));
}
function calcUpdatedTip(op) {
    var _a, _b, _c, _d, _e;
    // Closed Ops
    if (!!op.closedDate) {
        if (!!op.approvedDate)
            return (_a = op.approvedBy) === null || _a === void 0 ? void 0 : _a.title;
        if (opIs.missed(op))
            return undefined;
        return (_b = op.closedBy) === null || _b === void 0 ? void 0 : _b.title;
    }
    // Open Ops
    if (!!op.actualStart && op.actualStart.isSame(op.updatedDate))
        return (_c = op.startedBy) === null || _c === void 0 ? void 0 : _c.title;
    if (!!op.updatedDate)
        return (_d = op.updatedBy) === null || _d === void 0 ? void 0 : _d.title;
    return (_e = op.createdBy) === null || _e === void 0 ? void 0 : _e.title;
}
function calcAssignedTo(op) {
    if (opIs.closed(op))
        return undefined;
    return {
        all: op.allPTsPermitted,
        staff: op.staff,
        positions: op.positions,
        teams: op.teams,
    };
}
function calcStampLabel(op) {
    return opIs.closed(op)
        ? i18n.uc('closed')
        : opIs.approvable(op)
            ? i18n.uc('approve')
            : undefined;
}
